import React, { useEffect, useRef, useState } from "react";
import api from "@/api";
import {
  Save, Eye, EyeOff, CheckCircle, AlertCircle, Sliders,
  Mail, Palette, Send, Loader2, Image
} from "lucide-react";

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function Input({ ...props }) {
  return (
    <input
      className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
      {...props}
    />
  );
}

export default function GlobalConfig() {
  // ── Mistral / existing config ─────────────────────────────────────
  const [config, setConfig] = useState({});
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [rateLimitRequests, setRateLimitRequests] = useState(10);
  const [rateLimitWindow, setRateLimitWindow] = useState(5);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // ── App branding ──────────────────────────────────────────────────
  const [appName, setAppName] = useState("DocIntel");
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoB64, setLogoB64] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);
  const [savedBrand, setSavedBrand] = useState(false);
  const fileRef = useRef();

  // ── SMTP ──────────────────────────────────────────────────────────
  const [smtp, setSmtp] = useState({
    smtp_host: "", smtp_port: 587, smtp_username: "", smtp_password: "",
    smtp_use_tls: true, smtp_use_ssl: false, smtp_from_email: "", smtp_from_name: "",
  });
  const [smtpPasswordSet, setSmtpPasswordSet] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savedSmtp, setSavedSmtp] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/config"),
      api.get("/admin/smtp-config"),
      api.get("/admin/app-settings"),
    ]).then(([cfg, smtpCfg, branding]) => {
      setConfig(cfg.data);
      setBillingEnabled(cfg.data.billing_enabled || false);
      setRateLimitRequests(cfg.data.rate_limit_requests ?? 10);
      setRateLimitWindow(cfg.data.rate_limit_window_minutes ?? 5);

      const s = smtpCfg.data;
      setSmtp(prev => ({
        ...prev,
        smtp_host: s.smtp_host || "",
        smtp_port: s.smtp_port || 587,
        smtp_username: s.smtp_username || "",
        smtp_use_tls: s.smtp_use_tls !== undefined ? s.smtp_use_tls : true,
        smtp_use_ssl: s.smtp_use_ssl || false,
        smtp_from_email: s.smtp_from_email || "",
        smtp_from_name: s.smtp_from_name || "",
      }));
      setSmtpPasswordSet(s.smtp_password_set || false);

      setAppName(branding.data.app_name || "DocIntel");
      if (branding.data.app_logo_base64) {
        setLogoPreview(`data:image/png;base64,${branding.data.app_logo_base64}`);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // ── Save general config ───────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      const payload = { billing_enabled: billingEnabled, rate_limit_requests: rateLimitRequests, rate_limit_window_minutes: rateLimitWindow };
      if (apiKey) payload.mistral_api_key = apiKey;
      await api.put("/admin/config", payload);
      setSaved(true);
      setApiKey("");
      const r = await api.get("/admin/config");
      setConfig(r.data);
      setTimeout(() => setSaved(false), 3000);
    } finally { setSaving(false); }
  };

  // ── Logo upload ───────────────────────────────────────────────────
  const handleLogoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setLogoPreview(dataUrl);
      const b64 = dataUrl.split(",")[1];
      setLogoB64(b64);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveBrand = async () => {
    setSavingBrand(true);
    setSavedBrand(false);
    try {
      await api.put("/admin/app-settings", {
        app_name: appName,
        ...(logoB64 ? { app_logo_base64: logoB64 } : {}),
      });
      setSavedBrand(true);
      setTimeout(() => setSavedBrand(false), 3000);
      // Refresh context
      window.dispatchEvent(new Event("app-config-changed"));
    } finally { setSavingBrand(false); }
  };

  // ── Save SMTP ─────────────────────────────────────────────────────
  const handleSaveSmtp = async () => {
    setSavingSmtp(true);
    setSavedSmtp(false);
    try {
      const payload = { ...smtp };
      if (!payload.smtp_password) delete payload.smtp_password;  // don't overwrite if blank
      await api.put("/admin/smtp-config", payload);
      setSavedSmtp(true);
      setTimeout(() => setSavedSmtp(false), 3000);
    } finally { setSavingSmtp(false); }
  };

  const handleTestSmtp = async () => {
    if (!testRecipient) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/admin/smtp-test", { recipient: testRecipient });
      setTestResult({ ok: true, msg: r.data.message });
    } catch (err) {
      setTestResult({ ok: false, msg: err.response?.data?.detail || "Test failed" });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md bg-[#E5F0F2] flex items-center justify-center text-[#006080]">
          <Sliders size={16} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Platform Configuration</h2>
          <p className="text-sm text-slate-500">Global settings for the {appName} platform.</p>
        </div>
      </div>

      {saved && <StatusBanner ok>Configuration saved successfully.</StatusBanner>}

      <form onSubmit={handleSave} className="space-y-5">
        {/* Mistral */}
        <Section title="Mistral OCR Configuration">
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-md border border-slate-200">
            {config.mistral_api_key_set
              ? <><CheckCircle size={14} className="text-emerald-500" /><span className="text-sm text-slate-700">Key configured: <code className="regex-code">{config.mistral_api_key_masked}</code></span></>
              : <><AlertCircle size={14} className="text-amber-500" /><span className="text-sm text-amber-700">No global API key set.</span></>}
          </div>
          <Field label={config.mistral_api_key_set ? "Update Mistral API Key" : "Set Mistral API Key"} hint="Used as fallback when company doesn't have its own key.">
            <div className="relative">
              <input
                data-testid="mistral-api-key-input"
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder={config.mistral_api_key_set ? "Enter new key to update..." : "sk-..."}
                className="w-full h-10 px-3 pr-10 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white font-mono"
              />
              <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </Field>
        </Section>

        {/* Billing */}
        <Section title="Billing & Plans">
          <div className="flex items-center justify-between p-4 border border-slate-200 rounded-md">
            <div>
              <p className="text-sm font-medium text-slate-900">Enable Billing</p>
              <p className="text-xs text-slate-500">Toggle billing features platform-wide.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input data-testid="billing-toggle" type="checkbox" checked={billingEnabled} onChange={e => setBillingEnabled(e.target.checked)} className="sr-only peer" />
              <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#006080]" />
            </label>
          </div>
        </Section>

        {/* Rate Limiting */}
        <Section title="Public Upload Rate Limiting">
          <p className="text-xs text-slate-400">Limits are enforced per IP address. Set to 0 to disable.</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Max Uploads">
              <input data-testid="rate-limit-requests-input" type="number" min={0} max={1000} value={rateLimitRequests}
                onChange={e => setRateLimitRequests(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
            </Field>
            <Field label="Per (minutes)">
              <input data-testid="rate-limit-window-input" type="number" min={1} max={1440} value={rateLimitWindow}
                onChange={e => setRateLimitWindow(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
            </Field>
          </div>
          {rateLimitRequests > 0
            ? <p className="text-xs text-[#006080] bg-[#E5F0F2] border border-[#006080]/20 rounded px-3 py-2">Max <strong>{rateLimitRequests}</strong> uploads per <strong>{rateLimitWindow}</strong> min per IP.</p>
            : <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">Rate limiting is disabled.</p>}
        </Section>

        <button data-testid="save-config-button" type="submit" disabled={saving}
          className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50">
          <Save size={14} /> {saving ? "Saving..." : "Save Configuration"}
        </button>
      </form>

      {/* ── App Branding ──────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Palette size={14} className="text-[#006080]" />
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">App Branding</h3>
        </div>
        {savedBrand && <StatusBanner ok>Branding saved. Refresh to see changes everywhere.</StatusBanner>}

        <Field label="App Name" hint="Shown in sidebar, emails, landing page, and all references">
          <Input
            data-testid="app-name-input"
            value={appName}
            onChange={e => setAppName(e.target.value)}
            placeholder="DocIntel"
          />
        </Field>

        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Logo</label>
          <div className="flex items-center gap-4">
            {logoPreview ? (
              <div className="w-14 h-14 rounded-md border border-slate-200 flex items-center justify-center overflow-hidden bg-white p-1">
                <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-14 h-14 rounded-md border border-dashed border-slate-300 flex items-center justify-center text-slate-300 bg-slate-50">
                <Image size={18} />
              </div>
            )}
            <div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="h-8 px-3 text-xs font-medium border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-slate-600"
              >
                {logoPreview ? "Change Logo" : "Upload Logo"}
              </button>
              {logoPreview && (
                <button type="button" onClick={() => { setLogoPreview(null); setLogoB64(null); }} className="ml-2 text-xs text-red-500 hover:text-red-700">Remove</button>
              )}
              <p className="text-[10px] text-slate-400 mt-1">PNG, JPG, WebP — recommended 160×44px</p>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoFile} className="hidden" />
            </div>
          </div>
        </div>

        <button
          data-testid="save-branding-button"
          onClick={handleSaveBrand}
          disabled={savingBrand}
          className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
        >
          <Save size={14} /> {savingBrand ? "Saving..." : "Save Branding"}
        </button>
      </div>

      {/* ── SMTP Configuration ────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Mail size={14} className="text-[#006080]" />
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Email (SMTP) Configuration</h3>
        </div>
        {savedSmtp && <StatusBanner ok>SMTP settings saved.</StatusBanner>}

        <div className="grid grid-cols-2 gap-4">
          <Field label="SMTP Host">
            <Input data-testid="smtp-host-input" value={smtp.smtp_host} onChange={e => setSmtp(s => ({ ...s, smtp_host: e.target.value }))} placeholder="smtp.gmail.com" />
          </Field>
          <Field label="Port">
            <Input data-testid="smtp-port-input" type="number" value={smtp.smtp_port} onChange={e => setSmtp(s => ({ ...s, smtp_port: parseInt(e.target.value) || 587 }))} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Username">
            <Input data-testid="smtp-username-input" value={smtp.smtp_username} onChange={e => setSmtp(s => ({ ...s, smtp_username: e.target.value }))} placeholder="you@gmail.com" />
          </Field>
          <Field label={smtpPasswordSet ? "Password (leave blank to keep)" : "Password"}>
            <Input data-testid="smtp-password-input" type="password" value={smtp.smtp_password} onChange={e => setSmtp(s => ({ ...s, smtp_password: e.target.value }))} placeholder={smtpPasswordSet ? "••••••••" : "SMTP password or app password"} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="From Email">
            <Input data-testid="smtp-from-email-input" type="email" value={smtp.smtp_from_email} onChange={e => setSmtp(s => ({ ...s, smtp_from_email: e.target.value }))} placeholder="noreply@yourapp.com" />
          </Field>
          <Field label="From Name">
            <Input data-testid="smtp-from-name-input" value={smtp.smtp_from_name} onChange={e => setSmtp(s => ({ ...s, smtp_from_name: e.target.value }))} placeholder="DocIntel Notifications" />
          </Field>
        </div>
        <div className="flex items-center gap-6">
          {[["smtp_use_tls", "Use STARTTLS (port 587)"], ["smtp_use_ssl", "Use SSL (port 465)"]].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={smtp[key]}
                onChange={e => setSmtp(s => ({ ...s, [key]: e.target.checked }))}
                className="w-4 h-4 rounded border-slate-300 text-[#006080] focus:ring-[#006080]"
              />
              <span className="text-sm text-slate-600">{label}</span>
            </label>
          ))}
        </div>

        <button
          data-testid="save-smtp-button"
          onClick={handleSaveSmtp}
          disabled={savingSmtp}
          className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50"
        >
          <Save size={14} /> {savingSmtp ? "Saving..." : "Save SMTP Settings"}
        </button>

        {/* Test email */}
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Send Test Email</p>
          <div className="flex gap-2">
            <input
              data-testid="smtp-test-recipient"
              type="email"
              value={testRecipient}
              onChange={e => setTestRecipient(e.target.value)}
              placeholder="test@example.com"
              className="flex-1 h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
            />
            <button
              data-testid="smtp-test-button"
              onClick={handleTestSmtp}
              disabled={testing || !testRecipient}
              className="flex items-center gap-1.5 h-9 px-4 text-sm font-medium border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50 transition-colors text-slate-700"
            >
              {testing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {testing ? "Sending..." : "Send Test"}
            </button>
          </div>
          {testResult && (
            <div className={`mt-2 flex items-center gap-2 p-2.5 rounded-md text-sm ${testResult.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
              {testResult.ok ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
              {testResult.msg}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBanner({ ok, children }) {
  return (
    <div className={`flex items-center gap-2 p-3 rounded-md text-sm border ${ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
      {ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
      {children}
    </div>
  );
}
